// Warn at authoring time when a template uses variables the chosen trigger
// cannot supply.
//
// The renderer (shared by send_email and send_sms) builds its variable map
// section by section: `if (d.booking) { ... }`. A section the trigger never
// carries is simply absent, and any token in it renders as the literal
// "{{booking.date}}" in the message that reaches the customer. In email that is
// embarrassing; in SMS it is visible, billed as part of a segment, and cannot
// be recalled.
//
// `available_fields` on a trigger type comes in TWO shapes in production data:
//   array  ["booking_slot.id", "customer.first_name", "game.name"]
//   object {"ticket_id": "Ticket ID", "commenter_email": "Commenter Email"}
// Both are handled. Anything else returns null, meaning "unknown" — callers
// then warn about nothing, because a false warning on every automation is worse
// than the gap this closes.

// Populated by the renderer whenever a company group is in context, which is
// every trigger. These never need the trigger to carry an identifier.
const ALWAYS_AVAILABLE = ['companyGroup', 'company', 'system'];

// Trigger metadata prefix -> renderer section. Note booking_slot/booking: the
// trigger metadata and the variable namespace disagree on the name.
const PREFIX_TO_SECTION = {
  customer: 'customer',
  booking_slot: 'booking',
  booking: 'booking',
  game: 'game',
  transaction: 'transaction',
  created_by: 'staff',
  staff: 'staff',
  log: 'log',
  company_group: 'companyGroup',
  company: 'company',
};

// Object-shape keys are raw identifiers rather than dotted paths.
const IDENTIFIER_TO_SECTION = {
  customer_id: 'customer',
  booking_id: 'booking',
  booking_slot_id: 'booking',
  transaction_id: 'transaction',
  game_id: 'game',
  game_instance_id: 'game',
  company_user_id: 'staff',
};

/**
 * Sections the renderer will be able to populate for this trigger.
 * Returns null when available_fields is missing or an unrecognised shape.
 */
export function sectionsForTrigger(availableFields) {
  if (!availableFields) return null;

  const sections = new Set(ALWAYS_AVAILABLE);

  if (Array.isArray(availableFields)) {
    if (availableFields.length === 0) return null;
    availableFields.forEach((key) => {
      const prefix = String(key).split('.')[0];
      const section = PREFIX_TO_SECTION[prefix];
      if (section) sections.add(section);
    });
    return sections;
  }

  if (typeof availableFields === 'object') {
    const keys = Object.keys(availableFields);
    if (keys.length === 0) return null;
    keys.forEach((key) => {
      const section = IDENTIFIER_TO_SECTION[key];
      if (section) sections.add(section);
    });
    return sections;
  }

  return null;
}

/** Every {{token}} in a body, de-duplicated, in first-seen order. */
export function extractVariables(body) {
  const found = [];
  const seen = new Set();
  const re = /\{\{([^}]+)\}\}/g;
  let match = re.exec(body || '');
  while (match !== null) {
    const key = match[1].trim();
    if (key && !seen.has(key)) {
      seen.add(key);
      found.push(key);
    }
    match = re.exec(body || '');
  }
  return found;
}

/**
 * Variables in `body` whose section the trigger cannot supply.
 * Empty when the trigger's fields are unknown, so callers stay silent rather
 * than warning falsely.
 */
export function unresolvedVariables(body, availableFields) {
  const sections = sectionsForTrigger(availableFields);
  if (!sections) return [];

  return extractVariables(body).filter((key) => {
    // A token with no dot cannot be mapped to a section; leave it alone rather
    // than guessing.
    if (key.indexOf('.') === -1) return false;
    return !sections.has(key.split('.')[0]);
  });
}
